// ABOUTME: Hypotheses browse — all hypotheses grouped by theme, each a link to its detail
// ABOUTME: Statement-first and digestible; verdicts appear once interviews are analysed

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useHypotheses } from '@/hooks/useHypotheses';

const hNum = (name: string): number => {
  const n = parseInt((name || '').replace(/[^0-9]/g, ''), 10);
  return Number.isNaN(n) ? 9999 : n;
};

export const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { data: categories, isLoading, error } = useHypotheses();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-alx-blue/20 border-t-alx-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Couldn't load hypotheses. {error instanceof Error ? error.message : ''}
        </div>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8 text-gray-500">No hypotheses loaded yet.</div>
    );
  }

  const all = categories.flatMap((c: any) => c.hypotheses as any[]);

  const themeMap = new Map<string, any[]>();
  all.forEach((h) => {
    const t = h.theme || 'Other';
    if (!themeMap.has(t)) themeMap.set(t, []);
    themeMap.get(t)!.push(h);
  });
  const themes = Array.from(themeMap.entries())
    .map(([theme, hyps]) => ({ theme, hyps: [...hyps].sort((a, b) => hNum(a.name) - hNum(b.name)) }))
    .sort((a, b) => hNum(a.hyps[0]?.name) - hNum(b.hyps[0]?.name));

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-8 border-l-2 border-alx-green pl-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-alx-cyan">Listening Tour</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-alx-blue">Hypotheses</h1>
        <p className="mt-2 max-w-2xl leading-relaxed text-gray-600">
          {all.length} hypotheses across {themes.length} themes. Open any to read its statement and the
          signals to look for; verdicts appear once interviews are analysed.
        </p>
      </header>

      <div className="space-y-8">
        {themes.map(({ theme, hyps }) => (
          <section key={theme}>
            <div className="mb-3 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{theme}</h2>
              <span className="text-xs tabular-nums text-gray-400">{hyps.length}</span>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {hyps.map((h, i) => (
                <button
                  key={h.id}
                  onClick={() => navigate(`/dashboard/hypothesis/${h.category}/${h.name}`)}
                  className={`flex w-full items-start gap-4 px-5 py-4 text-left transition hover:bg-alx-cyan/5 ${
                    i > 0 ? 'border-t border-gray-100' : ''
                  }`}
                >
                  <span className="mt-0.5 shrink-0 rounded bg-alx-blue/5 px-2 py-0.5 font-mono text-xs font-semibold text-alx-blue">
                    {h.name}
                  </span>
                  <span className="flex-1 text-sm leading-relaxed text-gray-800">
                    {h.statement || 'Untitled hypothesis'}
                  </span>
                  {(h.analysis_count ?? 0) > 0 ? (
                    <span className="mt-0.5 shrink-0 rounded-full bg-alx-green/15 px-2 py-0.5 text-xs font-medium text-alx-blue">
                      Analysed
                    </span>
                  ) : (
                    <span className="mt-0.5 hidden shrink-0 text-xs text-gray-400 sm:block">
                      Not yet analysed
                    </span>
                  )}
                  <span className="mt-0.5 shrink-0 text-gray-300">›</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};
